/**
 * localStorage persistence helpers with namespacing.
 *
 * All app state stays on the device; nothing is uploaded except through the
 * Worker (and control/approval traffic is E2E encrypted before it leaves).
 */

const PREFIX = 'luminis.';

function getStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function storageKey(name: string): string {
  return `${PREFIX}${name}`;
}

export function loadJson<T>(name: string, fallback: T): T {
  const storage = getStorage();
  if (!storage) return fallback;
  try {
    const raw = storage.getItem(storageKey(name));
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function saveJson(name: string, value: unknown): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(storageKey(name), JSON.stringify(value));
  } catch {
    // Quota exceeded / private mode: keep the in-memory state alive.
  }
}

export function removeKey(name: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(storageKey(name));
  } catch {
    /* ignore */
  }
}

/** Remove every Luminis key (used by "clear local data" / lost-PIN reset). */
export function clearLocalData(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function sessionGet(name: string): string | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(storageKey(name));
  } catch {
    return null;
  }
}

export function sessionSet(name: string, value: string): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(storageKey(name), value);
  } catch {
    /* ignore */
  }
}

export function sessionRemove(name: string): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(storageKey(name));
  } catch {
    /* ignore */
  }
}
